import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO, SEO_CONFIG } from '@/components/ui/SEO';
import {
  Calendar,
  Clock,
  ArrowRight,
  Search,
  TrendingUp,
  BookOpen,
  Sparkles,
  Users,
  Bell,
  CheckCircle2,
  Code2,
  Brain,
  Shield,
  Rocket,
  Eye,
  MessageSquare,
  Tag,
  ChevronRight,
  Flame
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useTranslation } from 'react-i18next';

interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  image: string;
  author: {
    name: string;
    avatar: string;
    role: string;
  };
  date: string;
  readTime: string;
  category: string;
  tags: string[];
  views?: number;
  featured?: boolean;
  trending?: boolean;
}

// Category icons mapping (labels come from translations)
const categoryIcons = {
  all: BookOpen,
  ai: Brain,
  development: Code2,
  security: Shield,
  trends: TrendingUp,
  enterprise: Users,
};

const blogPosts: BlogPost[] = [
  {
    slug: 'yapay-zeka-yazilim-gelistirme-2025',
    title: 'Yapay Zeka ile Yazılım Geliştirme: 2025 Trendleri',
    excerpt: 'AI destekli kod editörleri, otomatik test üretimi ve akıllı refactoring araçları ile yazılım geliştirme süreçleri nasıl dönüşüyor? 2025\'in en önemli trendlerini inceliyoruz.',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=600&fit=crop',
    author: { name: 'Dr. Ahmet Yılmaz', avatar: 'AY', role: 'AI Araştırma Direktörü' },
    date: '25 Aralık 2024',
    readTime: '8 dk',
    category: 'ai',
    tags: ['Yapay Zeka', 'Yazılım', 'Trendler'],
    views: 3420,
    featured: true
  },
  {
    slug: 'turkce-llm-modelleri-gelecegi',
    title: 'Türkçe LLM Modelleri: Yerli AI\'ın Geleceği',
    excerpt: 'Türkçe\'ye özel optimize edilmiş büyük dil modelleri neden önemli? Ochtopus AI projemizden edindiğimiz deneyimler ve Türkçe NLP\'nin geleceği.',
    image: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1200&h=600&fit=crop',
    author: { name: 'Zeynep Kaya', avatar: 'ZK', role: 'NLP Mühendisi' },
    date: '22 Aralık 2024',
    readTime: '10 dk',
    category: 'ai',
    tags: ['NLP', 'Türkçe', 'LLM'],
    views: 2890,
    trending: true
  },
  {
    slug: 'kurumsal-dijital-donusum-rehberi',
    title: 'Kurumsal Dijital Dönüşüm: A\'dan Z\'ye Rehber',
    excerpt: 'Excel\'den bulut tabanlı sistemlere, legacy yazılımlardan modern mimarilere geçiş süreci. 8 yıllık deneyimimizden öğrendiklerimiz.',
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&h=600&fit=crop',
    author: { name: 'Mehmet Demir', avatar: 'MD', role: 'Çözüm Mimarı' },
    date: '20 Aralık 2024',
    readTime: '12 dk',
    category: 'enterprise',
    tags: ['Dijital Dönüşüm', 'Kurumsal', 'Cloud'],
    views: 1850
  },
  {
    slug: 'siber-guvenlik-tehditleri-2025',
    title: '2025\'te Dikkat Edilmesi Gereken Siber Güvenlik Tehditleri',
    excerpt: 'Ransomware, supply chain saldırıları ve AI destekli siber tehditler. Şirketinizi korumak için almanız gereken önlemler.',
    image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1200&h=600&fit=crop',
    author: { name: 'Can Arslan', avatar: 'CA', role: 'Güvenlik Uzmanı' },
    date: '18 Aralık 2024',
    readTime: '7 dk',
    category: 'security',
    tags: ['Siber Güvenlik', 'Ransomware', 'Tehditler'],
    views: 2340,
    trending: true
  },
  {
    slug: 'react-vs-vue-vs-angular-2025',
    title: 'React vs Vue vs Angular: 2025 Karşılaştırması',
    excerpt: 'Frontend framework seçimi hala zor mu? Performans, öğrenme eğrisi ve ekosistem açısından güncel karşılaştırma.',
    image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=1200&h=600&fit=crop',
    author: { name: 'Elif Özkan', avatar: 'EO', role: 'Frontend Lead' },
    date: '15 Aralık 2024',
    readTime: '9 dk',
    category: 'development',
    tags: ['React', 'Vue', 'Angular', 'Frontend'],
    views: 4120
  },
  {
    slug: 'microservices-vs-monolith',
    title: 'Microservices mi Monolith mi? Doğru Mimariyi Seçmek',
    excerpt: 'Her proje microservices mimarisine ihtiyaç duymaz. Hangi durumlarda hangi yaklaşımı tercih etmelisiniz?',
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1200&h=600&fit=crop',
    author: { name: 'Burak Şahin', avatar: 'BS', role: 'Backend Architect' },
    date: '12 Aralık 2024',
    readTime: '11 dk',
    category: 'development',
    tags: ['Microservices', 'Mimari', 'Backend'],
    views: 1980
  },
  {
    slug: 'e-ticaret-entegrasyon-stratejileri',
    title: 'E-Ticaret Entegrasyon Stratejileri: Çoklu Pazar Yeri Yönetimi',
    excerpt: 'Trendyol, Hepsiburada, Amazon Türkiye... Birden fazla pazar yerinde stok ve sipariş yönetimi nasıl yapılır?',
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1200&h=600&fit=crop',
    author: { name: 'Selin Yıldız', avatar: 'SY', role: 'E-Ticaret Danışmanı' },
    date: '10 Aralık 2024',
    readTime: '8 dk',
    category: 'enterprise',
    tags: ['E-Ticaret', 'Entegrasyon', 'Marketplace'],
    views: 1540
  },
  {
    slug: 'devops-best-practices',
    title: 'DevOps Best Practices: CI/CD Pipeline Optimizasyonu',
    excerpt: 'GitHub Actions, GitLab CI, Jenkins... Modern CI/CD pipeline\'ları nasıl optimize edilir? Gerçek dünya örnekleri.',
    image: 'https://images.unsplash.com/photo-1618401471353-b98afee0b2eb?w=1200&h=600&fit=crop',
    author: { name: 'Kerem Aktaş', avatar: 'KA', role: 'DevOps Engineer' },
    date: '8 Aralık 2024',
    readTime: '10 dk',
    category: 'development',
    tags: ['DevOps', 'CI/CD', 'Otomasyon'],
    views: 2100
  },
  {
    slug: 'ai-chatbot-gelistirme',
    title: 'AI Chatbot Geliştirme: Adım Adım Rehber',
    excerpt: 'Müşteri hizmetleri için AI chatbot nasıl geliştirilir? RAG, fine-tuning ve prompt engineering teknikleri.',
    image: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=1200&h=600&fit=crop',
    author: { name: 'Dr. Ahmet Yılmaz', avatar: 'AY', role: 'AI Araştırma Direktörü' },
    date: '5 Aralık 2024',
    readTime: '14 dk',
    category: 'ai',
    tags: ['Chatbot', 'RAG', 'NLP'],
    views: 3200
  }
];

// Popular tags
const popularTags = [
  { name: 'Yapay Zeka', count: 24 },
  { name: 'React', count: 18 },
  { name: 'DevOps', count: 15 },
  { name: 'Siber Güvenlik', count: 12 },
  { name: 'Cloud', count: 11 },
  { name: 'TypeScript', count: 10 }
];

export function BlogPage() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchRef = React.useRef<HTMLDivElement>(null);

  // Translated categories
  const categories = [
    { id: 'all', label: t('blog.categories.all'), icon: categoryIcons.all },
    { id: 'ai', label: t('blog.categories.ai'), icon: categoryIcons.ai },
    { id: 'development', label: t('blog.categories.development'), icon: categoryIcons.development },
    { id: 'security', label: t('blog.categories.security'), icon: categoryIcons.security },
    { id: 'trends', label: t('blog.categories.trends'), icon: categoryIcons.trends },
    { id: 'enterprise', label: t('blog.categories.enterprise'), icon: categoryIcons.enterprise },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get search results for dropdown
  const searchResults = searchQuery.length > 0
    ? blogPosts.filter(post =>
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      ).slice(0, 5)
    : [];

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = activeCategory === 'all' || post.category === activeCategory;
    const matchesSearch = searchQuery === '' ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredPost = filteredPosts.find(post => post.featured) || filteredPosts[0];
  // Get top 5 posts by views for trending section
  const trendingPosts = [...blogPosts]
    .sort((a, b) => (b.views || 0) - (a.views || 0))
    .slice(0, 5);
  const regularPosts = filteredPosts.filter(post => post !== featuredPost);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
    }
  };

  return (
    <>
      <SEO {...SEO_CONFIG.blog} />
      <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-fuchsia-500/5" />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }} />
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-fuchsia-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '8s', animationDelay: '2s' }} />

        {/* Floating Tech Elements */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Floating Code Brackets */}
          <div className="absolute top-[10%] left-[5%] text-purple-500/[0.04] text-6xl font-mono animate-float" style={{ animationDuration: '8s' }}>{'{ }'}</div>
          <div className="absolute top-[25%] right-[8%] text-fuchsia-500/[0.04] text-5xl font-mono animate-float" style={{ animationDuration: '10s', animationDelay: '1s' }}>{'< />'}</div>
          <div className="absolute bottom-[30%] left-[12%] text-violet-500/[0.04] text-4xl font-mono animate-float" style={{ animationDuration: '7s', animationDelay: '2s' }}>{'[ ]'}</div>
          <div className="absolute top-[60%] right-[15%] text-purple-500/[0.04] text-5xl font-mono animate-float" style={{ animationDuration: '9s', animationDelay: '3s' }}>{'( )'}</div>

          {/* Floating Hash Tags */}
          <div className="absolute top-[15%] left-[25%] text-purple-400/[0.04] text-2xl font-bold animate-float" style={{ animationDuration: '11s', animationDelay: '0.5s' }}>#AI</div>
          <div className="absolute top-[45%] right-[25%] text-fuchsia-400/[0.04] text-xl font-bold animate-float" style={{ animationDuration: '9s', animationDelay: '2.5s' }}>#Tech</div>
          <div className="absolute bottom-[20%] left-[30%] text-violet-400/[0.04] text-lg font-bold animate-float" style={{ animationDuration: '10s', animationDelay: '1.5s' }}>#Code</div>
          <div className="absolute top-[70%] left-[8%] text-purple-400/[0.04] text-xl font-bold animate-float" style={{ animationDuration: '8s', animationDelay: '4s' }}>#Dev</div>

          {/* Binary Rain Effect */}
          <div className="absolute top-0 left-[20%] text-purple-500/[0.03] text-xs font-mono leading-loose animate-scroll-down" style={{ animationDuration: '20s' }}>
            01001<br/>10110<br/>01011<br/>10101<br/>01100<br/>10010<br/>01101
          </div>
          <div className="absolute top-0 right-[30%] text-fuchsia-500/[0.03] text-xs font-mono leading-loose animate-scroll-down" style={{ animationDuration: '25s', animationDelay: '5s' }}>
            10110<br/>01001<br/>11010<br/>00101<br/>10011<br/>01110<br/>10001
          </div>

          {/* Floating Icons */}
          <div className="absolute top-[35%] left-[18%] animate-float" style={{ animationDuration: '12s', animationDelay: '1s' }}>
            <BookOpen className="text-purple-500/[0.04]" size={32} />
          </div>
          <div className="absolute top-[55%] right-[10%] animate-float" style={{ animationDuration: '10s', animationDelay: '3s' }}>
            <Code2 className="text-fuchsia-500/[0.04]" size={28} />
          </div>
          <div className="absolute bottom-[25%] right-[20%] animate-float" style={{ animationDuration: '11s', animationDelay: '2s' }}>
            <Brain className="text-violet-500/[0.04]" size={30} />
          </div>
          <div className="absolute top-[80%] left-[40%] animate-float" style={{ animationDuration: '9s', animationDelay: '4s' }}>
            <Sparkles className="text-purple-500/[0.04]" size={24} />
          </div>

          {/* Connection Lines */}
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(139, 92, 246)" stopOpacity="0.05" />
                <stop offset="50%" stopColor="rgb(217, 70, 239)" stopOpacity="0.08" />
                <stop offset="100%" stopColor="rgb(139, 92, 246)" stopOpacity="0.05" />
              </linearGradient>
            </defs>
            <path
              d="M0,200 Q400,100 800,300 T1600,200"
              fill="none"
              stroke="url(#lineGradient)"
              strokeWidth="1"
              className="animate-pulse"
              style={{ animationDuration: '4s' }}
            />
            <path
              d="M0,400 Q300,500 600,350 T1200,450"
              fill="none"
              stroke="url(#lineGradient)"
              strokeWidth="1"
              className="animate-pulse"
              style={{ animationDuration: '5s', animationDelay: '1s' }}
            />
          </svg>
        </div>
      </div>

      {/* Hero */}
      <section className="pt-32 pb-8 relative overflow-hidden">
        <div className="container relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 mb-6">
                <Sparkles size={16} className="text-purple-400" />
                <span className="text-sm font-medium text-purple-400">{t('blog.badge')}</span>
              </div>

              <h1 className="heading-xl mb-6">
                {t('blog.hero.title')}{' '}
                <span className="bg-gradient-to-r from-purple-400 via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">
                  {t('blog.hero.titleHighlight')}
                </span>{' '}
                {t('blog.hero.titleEnd')}
              </h1>
              <p className="text-lg text-text-secondary mb-8">
                {t('blog.hero.description')}
              </p>

              {/* Search */}
              <div className="relative max-w-lg" ref={searchRef}>
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary z-10" />
                <input
                  type="text"
                  placeholder={t('blog.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSearchDropdown(e.target.value.length > 0);
                  }}
                  onFocus={() => searchQuery.length > 0 && setShowSearchDropdown(true)}
                  className="w-full pl-12 pr-4 py-4 rounded-xl bg-bg-card border border-border-default focus:border-purple-500 focus:outline-none transition-colors"
                />

                {/* Search Dropdown */}
                {showSearchDropdown && searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-bg-card border border-border-default rounded-xl shadow-xl overflow-hidden z-50">
                    {searchResults.map((post) => (
                      <Link
                        key={post.slug}
                        to={`/blog/${post.slug}`}
                        onClick={() => {
                          setShowSearchDropdown(false);
                          setSearchQuery('');
                        }}
                        className="flex items-start gap-4 p-4 hover:bg-bg-elevated transition-colors border-b border-border-default last:border-b-0"
                      >
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-16 h-12 object-cover rounded-lg flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm text-text-primary line-clamp-1">{post.title}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-purple-400">{post.category}</span>
                            <span className="text-xs text-text-tertiary">• {post.readTime}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                {showSearchDropdown && searchQuery.length > 0 && searchResults.length === 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-bg-card border border-border-default rounded-xl shadow-xl p-4 z-50">
                    <p className="text-text-secondary text-sm text-center">{t('blog.noResults')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Trending Section */}
            <div className="card p-6 bg-gradient-to-br from-bg-card to-bg-surface">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center">
                  <Flame className="text-orange-400" size={18} />
                </div>
                <h3 className="font-semibold">{t('blog.trending')}</h3>
              </div>
              <div className="space-y-3">
                {trendingPosts.map((post, index) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="flex gap-3 group p-2 -mx-2 rounded-lg hover:bg-bg-elevated transition-colors"
                  >
                    <div className="text-lg font-bold text-purple-500/30 group-hover:text-purple-400 transition-colors w-6 flex-shrink-0 flex items-center">
                      0{index + 1}
                    </div>
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm group-hover:text-purple-400 transition-colors line-clamp-2">
                        {post.title}
                      </h4>
                      <div className="flex items-center gap-2 mt-1 text-xs text-text-tertiary">
                        <Eye size={10} />
                        <span>{post.views?.toLocaleString()}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-2 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-300',
                  activeCategory === category.id
                    ? 'bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white shadow-lg shadow-purple-500/25'
                    : 'bg-bg-card border border-border-default text-text-secondary hover:border-purple-500/50 hover:text-purple-400'
                )}
              >
                <category.icon size={16} />
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && (
        <section className="section-sm">
          <div className="container">
            <Link to={`/blog/${featuredPost.slug}`} className="block group">
              <div className="card p-0 overflow-hidden hover:border-purple-500/30 transition-all">
                <div className="grid lg:grid-cols-2">
                  <div className="relative aspect-video lg:aspect-auto overflow-hidden">
                    <img
                      src={featuredPost.image}
                      alt={featuredPost.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent lg:hidden" />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white text-sm font-medium shadow-lg">
                        {t('blog.featured')}
                      </span>
                    </div>
                  </div>
                  <div className="p-8 lg:p-10 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-purple-500/10 text-purple-400 text-sm font-medium">
                        <Brain size={14} />
                        {categories.find(c => c.id === featuredPost.category)?.label}
                      </span>
                      {featuredPost.views && (
                        <span className="text-xs text-text-tertiary flex items-center gap-1">
                          <Eye size={12} />
                          {featuredPost.views.toLocaleString()}
                        </span>
                      )}
                    </div>
                    <h2 className="text-2xl lg:text-3xl font-bold mb-4 group-hover:text-purple-400 transition-colors">
                      {featuredPost.title}
                    </h2>
                    <p className="text-text-secondary mb-6 text-lg line-clamp-3">
                      {featuredPost.excerpt}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {featuredPost.tags.map(tag => (
                        <span key={tag} className="px-2 py-1 rounded-md bg-bg-elevated text-xs text-text-tertiary">
                          #{tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center text-white font-semibold text-sm">
                          {featuredPost.author.avatar}
                        </div>
                        <div>
                          <span className="font-medium block text-sm">{featuredPost.author.name}</span>
                          <span className="text-text-tertiary text-xs">{featuredPost.author.role}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-text-tertiary text-sm">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {featuredPost.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {featuredPost.readTime}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Blog Grid */}
      <section className="section">
        <div className="container">
          <div className="grid lg:grid-cols-[1fr_300px] gap-12">
            {/* Posts Grid */}
            <div>
              <div className="grid md:grid-cols-2 gap-6">
                {regularPosts.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="group"
                  >
                    <article className="card h-full flex flex-col hover:border-purple-500/30 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-purple-500/5">
                      <div className="relative overflow-hidden rounded-xl aspect-video mb-4 -mx-6 -mt-6">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        {post.trending && (
                          <div className="absolute top-3 right-3">
                            <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-orange-500/90 text-white text-xs font-medium">
                              <Flame size={12} />
                              {t('blog.trendBadge')}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-purple-500/10 text-purple-400 text-xs font-medium">
                          {categories.find(c => c.id === post.category)?.label}
                        </span>
                        {post.views && (
                          <span className="text-xs text-text-tertiary flex items-center gap-1">
                            <Eye size={10} />
                            {post.views.toLocaleString()}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold mb-2 group-hover:text-purple-400 transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-text-secondary text-sm mb-4 flex-1 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between pt-4 border-t border-border-default">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center text-white text-xs font-semibold">
                            {post.author.avatar}
                          </div>
                          <span className="text-sm text-text-secondary">{post.author.name}</span>
                        </div>
                        <span className="text-text-tertiary text-xs flex items-center gap-1">
                          <Clock size={12} />
                          {post.readTime}
                        </span>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>

              {/* Load More */}
              <div className="text-center mt-12">
                <button className="btn btn-secondary">
                  {t('blog.loadMore')}
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="space-y-6">
              {/* Categories */}
              <div className="card">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <BookOpen size={18} className="text-purple-400" />
                  {t('blog.sidebar.categories')}
                </h3>
                <div className="space-y-2">
                  {categories.filter(c => c.id !== 'all').map((category) => {
                    const count = blogPosts.filter(p => p.category === category.id).length;
                    return (
                      <button
                        key={category.id}
                        onClick={() => setActiveCategory(category.id)}
                        className={cn(
                          'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors',
                          activeCategory === category.id
                            ? 'bg-purple-500/10 text-purple-400 border border-purple-500/30'
                            : 'hover:bg-bg-elevated text-text-secondary'
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <category.icon size={14} />
                          <span>{category.label}</span>
                        </div>
                        <span className="text-xs text-text-tertiary">({count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Popular Tags */}
              <div className="card">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Tag size={18} className="text-purple-400" />
                  {t('blog.sidebar.popularTags')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {popularTags.map(tag => (
                    <button
                      key={tag.name}
                      onClick={() => setSearchQuery(tag.name)}
                      className="px-3 py-1.5 rounded-full bg-bg-elevated border border-border-default text-sm text-text-secondary hover:border-purple-500/50 hover:text-purple-400 transition-colors"
                    >
                      {tag.name}
                      <span className="ml-1 text-text-tertiary">({tag.count})</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Newsletter */}
              <div className="card bg-gradient-to-br from-purple-500/10 to-fuchsia-500/5 border-purple-500/20">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center mb-4">
                  <Bell size={24} className="text-white" />
                </div>
                <h3 className="font-semibold mb-2">{t('blog.newsletter.title')}</h3>
                <p className="text-text-secondary text-sm mb-4">
                  {t('blog.newsletter.description')}
                </p>

                {subscribed ? (
                  <div className="flex items-center gap-2 text-green-400 py-2">
                    <CheckCircle2 size={20} />
                    <span className="text-sm font-medium">{t('blog.newsletter.success')}</span>
                  </div>
                ) : (
                  <form onSubmit={handleSubscribe} className="space-y-2">
                    <input
                      type="email"
                      placeholder={t('blog.newsletter.emailPlaceholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-bg-elevated border border-border-default focus:border-purple-500 focus:outline-none text-sm"
                    />
                    <button type="submit" className="btn btn-primary w-full btn-sm">
                      {t('blog.newsletter.subscribe')}
                    </button>
                  </form>
                )}
              </div>

              {/* Popular Articles */}
              <div className="card">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <TrendingUp size={18} className="text-purple-400" />
                  {t('blog.sidebar.popularArticles')}
                </h3>
                <div className="space-y-3">
                  {[...blogPosts]
                    .sort((a, b) => (b.views || 0) - (a.views || 0))
                    .slice(0, 10)
                    .map((post) => (
                      <Link
                        key={post.slug}
                        to={`/blog/${post.slug}`}
                        className="flex gap-3 group p-2 -mx-2 rounded-lg hover:bg-bg-elevated transition-colors"
                      >
                        <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={post.image}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm group-hover:text-purple-400 transition-colors line-clamp-2">
                            {post.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-purple-400">
                              {categories.find(c => c.id === post.category)?.label}
                            </span>
                            <span className="text-xs text-text-tertiary flex items-center gap-1">
                              <Eye size={10} />
                              {post.views?.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>

              {/* CTA Card */}
              <div className="card border-purple-500/20">
                <Rocket className="text-purple-400 mb-4" size={32} />
                <h3 className="font-semibold mb-2">{t('blog.cta.title')}</h3>
                <p className="text-text-secondary text-sm mb-4">
                  {t('blog.cta.description')}
                </p>
                <Link to="/contact" className="btn btn-primary btn-sm w-full">
                  {t('blog.cta.button')}
                  <ChevronRight size={16} />
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
